// WhatsAppWidget.js
import React, { useEffect, useState } from 'react';

const WhatsAppWidget = ({ phoneNumber, welcomeText, brandName }) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState(welcomeText || 'Hello! I need some help with your products.');

  useEffect(() => {
    // Add custom animation styles (Tailwind doesn't have pulse animation by default)
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse-whatsapp {
        0% {
          box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.4);
        }
        70% {
          box-shadow: 0 0 0 10px rgba(37, 211, 102, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
        }
      }
      .animate-pulse-whatsapp {
        animation: pulse-whatsapp 2s infinite;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  const handleWhatsAppClick = () => {
    const formattedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${formattedMessage}`, '_blank');
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-5 left-5 z-[1000] font-sans">
        {/* Tooltip */}
        <div 
          className={`absolute right-[70px] bottom-[15px] bg-white text-gray-800 px-4 py-2 rounded-full text-sm whitespace-nowrap shadow-md transition-all duration-300 ${
            isTooltipVisible ? 'opacity-100 visible' : 'opacity-0 invisible'
          } md:block hidden`}
          style={{ pointerEvents: 'none' }}
        >
          Chat with us on WhatsApp
          <div className="absolute right-[-8px] top-1/2 -translate-y-1/2 border-l-[6px] border-l-white border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
        </div>

        {/* WhatsApp Button */}
        <button
          onMouseEnter={() => setIsTooltipVisible(true)}
          onMouseLeave={() => setIsTooltipVisible(false)}
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center w-[60px] h-[60px] md:w-[60px] md:h-[60px] bg-[#25D366] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 animate-pulse-whatsapp cursor-pointer group"
        >
          <svg 
            className="w-[35px] h-[35px] md:w-[35px] md:h-[35px] fill-white transition-transform duration-300 group-hover:scale-110" 
            viewBox="0 0 24 24"
          >
            <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.563 1.098 3.677l-1.267 3.881 3.966-1.248c1.075.588 2.301.898 3.539.897 3.182 0 5.768-2.587 5.768-5.768-.001-3.181-2.587-5.768-5.768-5.768zm3.392 8.156c-.194.548-.789.856-1.324.856-.225 0-.451-.045-.662-.152-.419-.202-1.03-.51-1.724-.972-.682-.455-1.283-1.02-1.734-1.655-.478-.672-.739-1.429-.739-2.145 0-.832.391-1.607 1.074-2.129.144-.11.31-.182.488-.214.177-.032.359-.01.527.066.166.076.37.214.536.455.083.121.129.264.129.408 0 .144-.055.283-.151.391-.091.102-.188.194-.28.288-.094.095-.192.184-.282.281-.184.199-.191.476-.017.708.278.371.711.713 1.224 1.009.267.154.567.273.869.338.143.031.29.013.423-.051.133-.064.24-.166.307-.294.067-.129.099-.278.089-.428-.01-.151-.06-.291-.143-.418-.04-.062-.089-.118-.143-.17-.054-.052-.112-.099-.176-.14-.141-.09-.279-.193-.403-.311-.096-.092-.188-.188-.272-.291-.12-.145-.164-.329-.118-.5.046-.172.162-.318.314-.408.224-.133.48-.233.743-.302.24-.063.495-.091.747-.083.136.004.272.019.406.047.184.038.36.11.521.209.117.072.223.16.314.261.066.074.117.159.152.252.035.092.054.191.054.291 0 .101-.019.199-.057.291-.038.092-.094.176-.164.249-.07.074-.153.136-.244.185-.091.049-.187.085-.285.107-.098.022-.197.031-.294.031-.188 0-.373-.048-.537-.136-.141-.076-.263-.183-.36-.308-.097-.125-.167-.269-.202-.424-.035-.155-.033-.316.006-.468.039-.152.111-.291.209-.408.098-.117.216-.21.346-.276.13-.066.27-.104.411-.108.141-.004.281.025.413.084.132.059.247.147.338.255.091.108.155.235.188.371.033.136.035.278.006.416-.029.138-.085.268-.162.385-.077.117-.174.216-.285.294-.111.078-.233.133-.36.164-.127.031-.257.038-.385.021-.128-.017-.252-.059-.362-.122-.11-.063-.204-.147-.276-.246-.072-.099-.124-.21-.152-.327-.028-.117-.032-.238-.012-.356.02-.118.062-.23.12-.33.058-.1.131-.186.215-.254.084-.068.176-.117.274-.146.098-.029.199-.038.298-.027.099.011.195.043.285.092.09.049.167.114.231.191.064.077.111.165.14.258.036.116.05.237.042.357-.008.12-.038.237-.086.347-.048.11-.113.212-.191.302-.078.09-.168.167-.267.228-.099.061-.205.106-.316.133-.111.027-.225.036-.337.027-.112-.009-.222-.039-.324-.087-.102-.048-.194-.113-.273-.191-.079-.078-.144-.169-.193-.269-.049-.1-.082-.207-.098-.316-.016-.109-.015-.219.004-.327.019-.108.054-.212.104-.308.05-.096.113-.183.186-.258.073-.075.154-.138.242-.188.088-.05.18-.088.274-.112.094-.024.19-.035.285-.033.095.002.189.021.279.056.09.035.174.084.25.146.076.062.142.135.197.216.055.081.097.169.125.262.028.093.04.19.037.286-.003.096-.021.19-.054.279-.033.089-.079.171-.136.243-.057.072-.124.133-.198.183-.074.05-.153.089-.235.115-.082.026-.167.039-.251.039-.084 0-.167-.013-.246-.038-.079-.025-.152-.063-.217-.112-.065-.049-.121-.109-.166-.177-.045-.068-.078-.143-.098-.222-.02-.079-.028-.161-.023-.242.005-.081.021-.16.048-.235.027-.075.064-.145.109-.207.045-.062.098-.116.157-.16.059-.044.123-.078.191-.102.068-.024.139-.037.21-.038.071-.001.142.01.21.032.068.022.132.055.19.097.058.042.109.094.151.153.042.059.074.125.095.195.021.07.031.143.03.216-.001.073-.014.145-.038.213z"/>
          </svg>
        </button>
      </div>

      {/* Modal/Popup for Pre-chat Message */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[1100] flex items-center justify-center p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#075E54] text-white p-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 fill-white" viewBox="0 0 24 24">
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.563 1.098 3.677l-1.267 3.881 3.966-1.248c1.075.588 2.301.898 3.539.897 3.182 0 5.768-2.587 5.768-5.768-.001-3.181-2.587-5.768-5.768-5.768z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{brandName || 'Chat with Us'}</h3>
                  <p className="text-sm opacity-90">We usually reply within minutes</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Your Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent resize-none"
                  placeholder="Type your message here..."
                />
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  We'll respond on WhatsApp within 24 hours
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWhatsAppClick}
                  className="flex-1 px-4 py-2 bg-[#25D366] text-white rounded-lg hover:bg-[#20b859] transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.563 1.098 3.677l-1.267 3.881 3.966-1.248c1.075.588 2.301.898 3.539.897 3.182 0 5.768-2.587 5.768-5.768-.001-3.181-2.587-5.768-5.768-5.768z"/>
                  </svg>
                  Start Chat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsAppWidget;